import { CircularProgress } from '@mui/material';
import { useState } from 'react';
import { authenticatedFetch } from '../authentication/authenticatedFetch';
import { downloadFile } from '../project/ProjectsUtils';

export const downloadModelFile = async (modelId: string) => {
  const url = `${process.env.REACT_APP_API_URL}/models/${encodeURIComponent(
    modelId,
  )}/original.xlsx`;

  const response = await authenticatedFetch(url, {
    method: 'GET',
    headers: new Headers({
      Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
  });

  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`);
  }

  const fileBlob = await response.blob();

  const contentDisposition = response.headers.get('content-disposition');
  const matchedFilename =
    contentDisposition?.match(/filename="?([^"]+)"?/i)?.[1];

  const filename =
    matchedFilename ||
    `model_${modelId}_${new Date().toISOString().replace(/[:.]/g, '-')}.xlsx`;

  downloadFile(fileBlob, filename);
};

const DOWNLOAD_PREFIX = 'model-download/';

interface MarkdownLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href?: string;
  children: React.ReactNode;
}

const MarkdownLink = ({ href, children, ...props }: MarkdownLinkProps) => {
  const [loadingHref, setLoadingHref] = useState<string | null>(null);

  const isDownloadLink =
    typeof href === 'string' && href.startsWith(DOWNLOAD_PREFIX);

  if (!isDownloadLink) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    );
  }

  const modelId = href.slice(DOWNLOAD_PREFIX.length);
  const isLoading = loadingHref === href;

  const handleClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    if (!modelId || isLoading) return;

    try {
      setLoadingHref(href);
      await downloadModelFile(modelId);
    } catch (error) {
      console.error('Unable to download model file', error);
    } finally {
      setLoadingHref(null);
    }
  };

  return (
    <a
      href="#"
      className="markdown-download-link"
      onClick={handleClick}
      {...props}
    >
      {children}
      {isLoading && <CircularProgress sx={{ maxWidth: 16, maxHeight: 16 }} />}
    </a>
  );
};

export default MarkdownLink;